import type { RequestContext } from "../context/request-context.js";
import type { AdminRepository, ServiceFront } from "../../administration/admin-domain.js";
import type { ProjectRepository } from "../../infrastructure/database/memory/in-memory-project-repository.js";
import type { RdoRepository } from "../../repositories/operational-repositories.js";
import type { ClientId, ServiceFrontId } from "../../shared/kernel/identifiers.js";
import {
  filterFrontsByProject,
  resolveFrontProjectId,
  selectableFrontsForProject,
  validateServiceFrontInput,
  FRONT_CLIENT_IS_FIXED,
  FRONT_IN_USE_MESSAGE,
  FRONT_PROJECT_OF_OTHER_CLIENT,
  FRONT_REQUIRES_CLIENT,
  type ResolvedServiceFront,
  type ServiceFrontInput,
  type ServiceFrontStatus
} from "../../domain/rdo/service-front-rules.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

/**
 * GESTÃO → FRENTES DE SERVIÇO.
 *
 * A Frente pertence a um Projeto; o Cliente é derivado dele. Frente com
 * histórico de RDO nunca é apagada — é inativada, para não destruir
 * apontamento já registrado.
 */
export class ServiceFrontService {
  constructor(
    private readonly adminRepo: AdminRepository,
    private readonly projectRepo: ProjectRepository,
    private readonly rdoRepo: RdoRepository,
    private readonly auth: RbacAuthorizationService
  ) {}

  private authorizedClientIdsResolver: ((context: RequestContext) => Promise<readonly ClientId[]>) | null = null;
  private clientNameLookup: (() => Promise<ReadonlyMap<string, string>>) | null = null;

  /**
   * "Todos os Clientes" (contexto sem Cliente) é a união dos Clientes
   * AUTORIZADOS à sessão. Sem o resolver, a listagem sem Cliente falha
   * fechada — antes ela devolvia as Frentes de todos os tenants.
   */
  public attachAuthorizedClientIdsResolver(resolver: (context: RequestContext) => Promise<readonly ClientId[]>): void {
    this.authorizedClientIdsResolver = resolver;
  }

  /** Nome do Cliente para a coluna CLIENTE da listagem (id → nome). */
  public attachClientNameLookup(lookup: () => Promise<ReadonlyMap<string, string>>): void {
    this.clientNameLookup = lookup;
  }

  private async scopeClientIds(context: RequestContext): Promise<ClientId[]> {
    if (context.activeClientId) return [context.activeClientId];
    if (!this.authorizedClientIdsResolver) return [];
    return [...new Set((await this.authorizedClientIdsResolver(context)).map(id => String(id)))] as ClientId[];
  }

  /** Frentes do Cliente do contexto — ou dos Clientes autorizados —, com Projeto e Cliente resolvidos. */
  async listFronts(
    context: RequestContext,
    filters: { projectId?: string; includeInactive?: boolean } = {}
  ): Promise<ResolvedServiceFront[]> {
    const hasReadPerm =
      context.permissions.has(PermissionCode.AdministrationRead) ||
      context.permissions.has(PermissionCode.RdoRead) ||
      context.permissions.has(PermissionCode.RdoCreate) ||
      context.permissions.has(PermissionCode.ProjectRead) ||
      context.permissions.has(PermissionCode.ClientAccess);
    if (!hasReadPerm) {
      this.auth.require(context, PermissionCode.AdministrationRead);
    }

    const clientIds = await this.scopeClientIds(context);
    const perClient = await Promise.all(clientIds.map(async clientId => ({
      fronts: await this.adminRepo.getServiceFronts(clientId),
      workOrders: await this.adminRepo.getWorkOrders(clientId),
      projects: await this.projectRepo.listProjects({ clientId })
    })));
    const fronts = perClient.flatMap(c => c.fronts);
    const workOrders = perClient.flatMap(c => c.workOrders);
    const projectById = new Map(perClient.flatMap(c => c.projects).map(p => [p.id, p]));
    const clientNames = this.clientNameLookup ? await this.clientNameLookup() : new Map<string, string>();

    const resolved = fronts.map(front => {
      const projectId = resolveFrontProjectId(front, workOrders.map(wo => ({ id: wo.id, projectId: wo.projectId })));
      const project = projectId ? projectById.get(projectId) ?? null : null;
      return {
        id: front.id,
        projectId,
        projectName: project ? `${project.code} — ${project.name}` : "Projeto não vinculado",
        clientId: front.clientId,
        clientName: clientNames.get(String(front.clientId)) ?? "",
        code: front.code ?? "",
        name: front.name,
        description: front.description ?? null,
        startDate: front.startDate ?? null,
        endDate: front.endDate ?? null,
        notes: front.notes ?? null,
        status: front.status as ServiceFrontStatus
      } satisfies ResolvedServiceFront;
    });

    const scoped = filters.projectId ? filterFrontsByProject(resolved, filters.projectId) : resolved;
    const visible = filters.includeInactive ? scoped : scoped.filter(f => f.status === "ACTIVE");

    return visible.sort((a, b) => (a.code || a.name).localeCompare(b.code || b.name, "pt-BR", { numeric: true }));
  }

  /** Frentes que o RDO pode oferecer: ATIVAS e do Projeto selecionado. */
  async listSelectableFronts(context: RequestContext, projectId: string): Promise<ResolvedServiceFront[]> {
    const all = await this.listFronts(context, { includeInactive: true });
    return selectableFrontsForProject(all, projectId);
  }

  async getFront(context: RequestContext, id: string): Promise<ServiceFront | null> {
    const hasReadPerm =
      context.permissions.has(PermissionCode.AdministrationRead) ||
      context.permissions.has(PermissionCode.RdoRead) ||
      context.permissions.has(PermissionCode.RdoCreate) ||
      context.permissions.has(PermissionCode.ProjectRead) ||
      context.permissions.has(PermissionCode.ClientAccess);
    if (!hasReadPerm) {
      this.auth.require(context, PermissionCode.AdministrationRead);
    }
    for (const clientId of await this.scopeClientIds(context)) {
      const found = (await this.adminRepo.getServiceFronts(clientId)).find(f => f.id === id);
      if (found) return found;
    }
    return null;
  }

  async createFront(context: RequestContext, input: ServiceFrontInput): Promise<ServiceFront> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    validateServiceFrontInput(input);

    // O Cliente é o do contexto da chamada (o servidor já conferiu o acesso
    // da sessão a ele) e o Projeto tem de ser DESSE Cliente: um request
    // manual com Cliente A + Projeto de B é recusado aqui, não só na tela.
    const clientId = context.activeClientId;
    if (!clientId) {
      throw new AppError(ErrorCode.Validation, FRONT_REQUIRES_CLIENT, { safeMessage: FRONT_REQUIRES_CLIENT });
    }
    const project = await this.projectRepo.getProject(input.projectId);
    if (!project) {
      throw new AppError(ErrorCode.NotFound, `Project ${input.projectId} not found`, {
        safeMessage: "O Projeto selecionado não foi encontrado."
      });
    }
    if (String(project.clientId) !== String(clientId)) {
      throw new AppError(ErrorCode.Validation, FRONT_PROJECT_OF_OTHER_CLIENT, { safeMessage: FRONT_PROJECT_OF_OTHER_CLIENT });
    }

    const now = new Date().toISOString();
    const front: ServiceFront = {
      id: `sf-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}` as ServiceFrontId,
      clientId: project.clientId as ClientId,
      projectId: project.id,
      workOrderId: null,
      subWorkOrderId: null,
      code: input.code.trim(),
      name: input.name.trim(),
      description: input.description?.trim() || null,
      startDate: input.startDate || null,
      endDate: input.endDate || null,
      notes: input.notes?.trim() || null,
      status: input.status ?? "ACTIVE",
      createdAt: now,
      updatedAt: now
    };

    await this.adminRepo.saveServiceFront(front);
    return front;
  }

  async updateFront(context: RequestContext, id: string, input: ServiceFrontInput): Promise<ServiceFront> {
    this.auth.require(context, PermissionCode.AdministrationManage);
    validateServiceFrontInput(input);

    const existing = await this.getFront(context, id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, `Service front ${id} not found`, {
        safeMessage: "Frente de Serviço não encontrada."
      });
    }

    const project = await this.projectRepo.getProject(input.projectId);
    if (!project) {
      throw new AppError(ErrorCode.NotFound, `Project ${input.projectId} not found`, {
        safeMessage: "O Projeto selecionado não foi encontrado."
      });
    }
    // Na edição o Cliente é fixo: o novo Projeto tem de ser do MESMO Cliente.
    if (String(project.clientId) !== String(existing.clientId)) {
      throw new AppError(ErrorCode.Validation, FRONT_CLIENT_IS_FIXED, { safeMessage: FRONT_CLIENT_IS_FIXED });
    }

    const updated: ServiceFront = {
      ...existing,
      clientId: project.clientId as ClientId,
      projectId: project.id,
      code: input.code.trim(),
      name: input.name.trim(),
      description: input.description?.trim() || null,
      startDate: input.startDate || null,
      endDate: input.endDate || null,
      notes: input.notes?.trim() || null,
      status: input.status ?? existing.status,
      updatedAt: new Date().toISOString()
    };

    await this.adminRepo.saveServiceFront(updated);
    return updated;
  }

  async setStatus(context: RequestContext, id: string, status: ServiceFrontStatus): Promise<ServiceFront> {
    this.auth.require(context, PermissionCode.AdministrationManage);

    const existing = await this.getFront(context, id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, `Service front ${id} not found`, {
        safeMessage: "Frente de Serviço não encontrada."
      });
    }

    const updated: ServiceFront = { ...existing, status, updatedAt: new Date().toISOString() };
    await this.adminRepo.saveServiceFront(updated);
    return updated;
  }

  /** Apontamentos de RDO que referenciam a Frente — inclusive cancelados, que são histórico. */
  async countUsage(context: RequestContext, id: string): Promise<number> {
    this.auth.require(context, PermissionCode.AdministrationRead);
    const rdos = await this.rdoRepo.find(context, { serviceFrontId: id });
    return rdos.length;
  }

  /**
   * Exclusão física só quando a Frente nunca foi usada. Havendo histórico, a
   * operação é recusada com a orientação de inativar.
   */
  async deleteFront(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AdministrationManage);

    const existing = await this.getFront(context, id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, `Service front ${id} not found`, {
        safeMessage: "Frente de Serviço não encontrada."
      });
    }

    const usage = await this.countUsage(context, id);
    if (usage > 0) {
      throw new AppError(ErrorCode.Conflict, `Service front ${id} has ${usage} RDO entries`, {
        safeMessage: FRONT_IN_USE_MESSAGE
      });
    }

    if (this.adminRepo.deleteServiceFront) {
      await this.adminRepo.deleteServiceFront(existing.clientId, id as ServiceFrontId);
      return;
    }

    // Repositório sem exclusão física: a Frente é inativada, nunca perdida.
    await this.setStatus(context, id, "INACTIVE");
  }
}
